import React, { useState, useEffect, useRef } from 'react';
import { Bot, Mail, FileSpreadsheet, Calculator, Clock, Zap, Download, Wifi, WifiOff, Play, X, AlertTriangle, CheckCircle, Loader, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

export function AgentiPage() {
  const [agentStatus, setAgentStatus] = useState<'unknown'|'online'|'offline'>('unknown');
  const [showOpominiModal, setShowOpominiModal] = useState(false);
  const [showInstallerModal, setShowInstallerModal] = useState(false);
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentLog, setAgentLog] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('aicount_agent_log') || '[]'); } catch { return []; }
  });
  const [confirmPending, setConfirmPending] = useState<any>(null);
  const [opominiParams, setOpominiParams] = useState({ baze: '7,8', datum_od: '', datum_do: '' });

  // Ping vsake 10s
  useEffect(() => {
    const preveri = async () => {
      try {
        await api.get('/agenti/ping');
        setAgentStatus('online');
      } catch {
        setAgentStatus('offline');
      }
    };
    preveri();
    const i = setInterval(preveri, 10000);
    return () => clearInterval(i);
  }, []);

  // Polling statusa in loga vsake 2s
  useEffect(() => {
    const i = setInterval(async () => {
      try {
        const r = await api.get('/agenti/status');
        const log = (r.data.log || []).map((l: any) => typeof l === 'string' ? l : JSON.stringify(l));
        // Posodobi log samo če agent dela
        setAgentLog(prev => {
          if (!r.data.running && prev.length === 0) return prev;
          if (r.data.running && log.length > 0) {
            try { localStorage.setItem('aicount_agent_log', JSON.stringify(log)); } catch {}
            return log;
          }
          if (r.data.running) return log;
          return prev; // Agent ne dela - ohrani obstoječi log
        });

        // Preveri ali je agent zaključil
        const zadnji = log[log.length - 1] || '';
        if (zadnji.includes('RESULT:done')) {
          setAgentRunning(false);
          setConfirmPending(null);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(i);
  }, []);

  // Polling za confirm_required
  useEffect(() => {
    const i = setInterval(async () => {
      try {
        const r = await api.get('/agenti/pending');
        if (r.data.pending) {
          setConfirmPending(r.data);
          setAgentRunning(true);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(i);
  }, []);



  const zaženiOpomini = async () => {
    const baze = opominiParams.baze.split(',').map(b => parseInt(b.trim())).filter(b => !isNaN(b));
    if (baze.length === 0) { toast.error('Vnesite vsaj eno bazo!'); return; }
    setShowOpominiModal(false);
    setAgentRunning(true);
    setAgentLog(prev => {
      const sep = [`--- Nov zagon: ${new Date().toLocaleString('sl-SI')} ---`];
      const combined = [...prev, ...sep];
      try { localStorage.setItem('aicount_agent_log', JSON.stringify(combined)); } catch {}
      return combined;
    });
    try {
      await api.post('/agenti/opomini', { baze, datum_od: opominiParams.datum_od, datum_do: opominiParams.datum_do });
    } catch (e: any) {
      const msg = e.response?.data?.detail || '';
      if (msg && msg !== 'None') toast.error(msg);
    }
  };

  const potrdiAgenta = async (ok: boolean) => {
    try {
      await api.post('/agenti/confirm', { ok });
      setConfirmPending(null);
      if (ok) {
        toast.success('Posiljanje potrjeno!');
      } else {
        toast('Agent preklican');
        setAgentRunning(false);
      }
    } catch {}
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Agenti</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Avtonomni agenti za računovodske procese</p>
        </div>
        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 px-3 py-1.5 rounded-full text-sm font-medium">
          <Zap className="w-4 h-4" />Beta
        </div>
      </div>

      {/* Status agenta */}
      <div className={clsx('rounded-2xl p-4 border flex items-center justify-between',
        agentStatus === 'online' ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
        : agentStatus === 'offline' ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
        : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800')}>
        <div className="flex items-center gap-3">
          {agentStatus === 'online' ? <Wifi className="w-5 h-5 text-green-600" />
          : agentStatus === 'offline' ? <WifiOff className="w-5 h-5 text-red-500" />
          : <Loader className="w-5 h-5 text-gray-400 animate-spin" />}
          <div>
            <p className={clsx('font-semibold text-sm',
              agentStatus === 'online' ? 'text-green-700 dark:text-green-300'
              : agentStatus === 'offline' ? 'text-red-700 dark:text-red-300'
              : 'text-gray-500')}>
              {agentStatus === 'online' ? 'Agent povezan' : agentStatus === 'offline' ? 'Agent ni povezan' : 'Preverjam...'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {agentStatus === 'online' ? (agentRunning ? '⚡ Agent dela...' : 'Pripravljen') : 'Namestite AlfaBerg Agent'}
            </p>
          </div>
        </div>
        {agentStatus !== 'online' && (
          <button onClick={() => setShowInstallerModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition">
            <Download className="w-4 h-4" />Prenesi Agent
          </button>
        )}
      </div>

      {/* POTRDITEV - prominentno obvestilo */}
      {confirmPending && (
        <div className="bg-amber-50 dark:bg-amber-950 border-2 border-amber-400 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900 dark:text-amber-100">Potrebna potrditev!</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">{confirmPending.message}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => potrdiAgenta(false)}
              className="flex-1 py-2.5 border border-amber-300 text-amber-700 dark:text-amber-300 rounded-xl text-sm hover:bg-amber-100 transition flex items-center justify-center gap-2">
              <XCircle className="w-4 h-4" />Preklic
            </button>
            <button onClick={() => potrdiAgenta(true)}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4" />Potrdi posiljanje
            </button>
          </div>
        </div>
      )}

      {/* LOG TRACKER - prikaži med izvajanjem */}
      {(agentRunning || agentLog.length > 0) && <div className="bg-gray-900 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {agentRunning ? <Loader className="w-4 h-4 text-green-400 animate-spin" /> : <span className="w-4 h-4">📋</span>}
              <span className="text-green-400 text-sm font-medium">{agentRunning ? 'Agent dela...' : 'Log agenta'}</span>
            </div>
            <button onClick={async () => { await api.post('/agenti/confirm', {ok: false}); setAgentRunning(false); setConfirmPending(null); setAgentLog([]); toast('Agent preklican'); }}
              className="flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs transition">
              <XCircle className="w-3 h-3" />Prekini
            </button>
            <button onClick={async () => { setAgentLog([]); localStorage.removeItem('aicount_agent_log'); sessionStorage.setItem('log_cleared','1'); try { await api.post('/agenti/clear-log'); } catch {} }}
              className="flex items-center gap-1 px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-xs transition">
              🗑 Počisti log
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto font-mono text-xs text-green-400 space-y-0.5" ref={el => { if(el) el.scrollTop = el.scrollHeight; }}>
            {agentLog.map((l, i) => (
              <div key={i} className={clsx(
                l.includes('NAPAKA') ? 'text-red-400' :
                l.includes('CONFIRM') ? 'text-amber-400' :
                l.includes('OK:') || l.includes('ZAKLJUCENO') ? 'text-green-300' : 'text-green-500'
              )}>{l}</div>
            ))}
          </div>
      </div>}

      {/* Intro */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Bot className="w-8 h-8" />
          <h2 className="text-lg font-semibold">Kaj so AI Agenti?</h2>
        </div>
        <p className="text-blue-100 text-sm leading-relaxed">
          AI Agenti so avtonomni programi ki izvajajo računovodske naloge namesto vas.
          Kliknete gumb — agent se poveže z vašimi sistemi, pridobi podatke in opravi nalogo.
        </p>
      </div>

      {/* Agenti grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400">
              <Mail className="w-6 h-6" />
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full font-medium">Na voljo</span>
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Opomini</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Samodejno pošiljanje opominov za neplačane račune v Vascu</p>
          <button onClick={() => agentStatus === 'online' ? setShowOpominiModal(true) : setShowInstallerModal(true)}
            disabled={agentRunning}
            className={clsx('w-full py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2',
              agentRunning ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white')}>
            {agentRunning ? <><Loader className="w-4 h-4 animate-spin"/>Agent dela...</> : <><Play className="w-4 h-4"/>Zaženi agenta</>}
          </button>
        </div>

        {[{icon: FileSpreadsheet, label:'IOP', desc:'Izpis odprtih postavk iz GK Vasco', color:'green'},
          {icon: Calculator, label:'DDV-O obračun', desc:'Priprava mesečnega DDV obračuna', color:'purple'},
          {icon: Clock, label:'Opomniki', desc:'Davčni roki in opomniki za stranke', color:'amber'}
        ].map(({icon:Icon, label, desc, color}) => (
          <div key={label} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 opacity-60">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-50 dark:bg-${color}-950 text-${color}-600 dark:text-${color}-400`}>
                <Icon className="w-6 h-6" />
              </div>
              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-1 rounded-full">Kmalu</span>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{label}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{desc}</p>
            <button disabled className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed">V razvoju</button>
          </div>
        ))}
      </div>

      {/* Modal - Opomini */}
      {showOpominiModal && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-gray-900 dark:text-white">Agent Opomini</h3>
              </div>
              <button onClick={() => setShowOpominiModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Baze (ločene z vejico)</label>
                <input value={opominiParams.baze}
                  onChange={e => setOpominiParams(p => ({...p, baze: e.target.value}))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:border-blue-500"
                  placeholder="7,8"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Datum od (neobvezno)</label>
                  <input type="date" value={opominiParams.datum_od}
                    onChange={e => setOpominiParams(p => ({...p, datum_od: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:border-blue-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Datum do (neobvezno)</label>
                  <input type="date" value={opominiParams.datum_do}
                    onChange={e => setOpominiParams(p => ({...p, datum_do: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:border-blue-500"/>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowOpominiModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl text-sm hover:border-gray-300 transition">
                  Zapri
                </button>
                <button onClick={zaženiOpomini}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2">
                  <Play className="w-4 h-4"/>Zaženi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Installer */}
      {showInstallerModal && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white">Namestite AlfaBerg Agent</h3>
              <button onClick={() => setShowInstallerModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950 rounded-xl p-4 text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-2">AlfaBerg Agent je potreben za:</p>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Avtomatske opomine v Vascu</li>
                  <li>Izpis odprtih postavk (IOP)</li>
                </ul>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-xs text-gray-500 space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300">Navodila:</p>
                <p>1. Prenesite ZIP na računalnik z Vascom</p>
                <p>2. Razpakirajte in zaženite NAMESTI.bat kot administrator</p>
                <p>3. Na namizju se pojavi bližnjica AlfaBerg Agent</p>
              </div>
              <a href={`${API_BASE}/agenti/installer`} download
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition">
                <Download className="w-4 h-4" />Prenesi AlfaBerg Agent (ZIP)
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
